import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ClipboardCopy, Copy, Mail, Phone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { TelegramIcon, ViberIcon, WhatsAppIcon } from './BrandIcons';

// The first is what every link and the copy button carry; the rest are a pick away.
export const GREETING_LANGUAGES = ['uk', 'en'];

// Icons only, in one row. The number arrives in E.164, which is what every
// messenger link wants.
export default function ContactButtons({
  phone,
  email,
  greetingNames,
  greetingScript,
  size = 'normal',
}) {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const [copied, setCopied] = useState(null);
  const [picking, setPicking] = useState(false);
  const picker = useRef(null);
  const iconSize = size === 'compact' ? 18 : 20;

  useEffect(() => {
    if (!picking) return undefined;
    const close = (event) => {
      if (event.type === 'keydown' ? event.key === 'Escape' : !picker.current?.contains(event.target)) setPicking(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [picking]);

  const sender = user?.name?.trim() || '';
  // Written in the language picked for the client, not in whatever the operator set the CRM to.
  const greetingIn = (language) => {
    const name = greetingNames && greetingScript ? greetingNames[greetingScript[language]] : null;
    return name
      ? t('crm.contact.greeting', { name, sender, lng: language })
      : t('crm.contact.greetingNoName', { sender, lng: language });
  };
  const greeting = greetingIn(GREETING_LANGUAGES[0]);

  const copy = async (value, what) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(what);
      setTimeout(() => setCopied((current) => (current === what ? null : current)), 2000);
    } catch {
      setCopied('failed');
    }
  };

  // The link still opens the messenger; the greeting goes along on the clipboard.
  const takeGreeting = () => copy(greeting, 'greeting');
  const takeGreetingIn = (language) => {
    setPicking(false);
    copy(greetingIn(language), 'greeting');
  };

  const greetingButtons = (
    <div className="crm-greeting" ref={picker}>
      <button
        type="button"
        className="crm-contact-btn greeting"
        data-tip={copied === 'greeting' ? t('crm.contact.copied') : t('crm.contact.copyGreeting')}
        aria-label={t('crm.contact.copyGreeting')}
        onClick={takeGreeting}
      >
        {copied === 'greeting' ? <Check size={iconSize} /> : <ClipboardCopy size={iconSize} />}
      </button>
      <button
        type="button"
        className="crm-greeting-pick"
        data-tip={t('crm.contact.copyGreetingIn')}
        aria-label={t('crm.contact.copyGreetingIn')}
        aria-haspopup="menu"
        aria-expanded={picking}
        onClick={() => setPicking(!picking)}
      >
        <ChevronDown size={14} />
      </button>
      {picking && (
        <div className="crm-greeting-menu" role="menu">
          {GREETING_LANGUAGES.map((language) => (
            <button key={language} type="button" role="menuitem" onClick={() => takeGreetingIn(language)}>
              {t(`crm.contact.language.${language}`)}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const status = {
    greeting: t('crm.contact.greetingCopied'),
    failed: t('crm.contact.copyFailed'),
  }[copied];

  if (!phone) {
    if (!email) return null;
    return (
      <div className={`crm-contact crm-contact-${size}`}>
        <a
          className="crm-contact-btn email"
          href={`mailto:${email}?body=${encodeURIComponent(greeting)}`}
          data-tip={email}
          aria-label={email}
          onClick={takeGreeting}
        >
          <Mail size={iconSize} />
        </a>
        {greetingButtons}
        <button
          type="button"
          className="crm-contact-btn copy"
          data-tip={copied === 'contact' ? t('crm.contact.copied') : t('crm.contact.copyEmail')}
          aria-label={t('crm.contact.copyEmail')}
          onClick={() => copy(email, 'contact')}
        >
          {copied === 'contact' ? <Check size={iconSize} /> : <Copy size={iconSize} />}
        </button>
        {status && <p className="crm-contact-status" role="status">{status}</p>}
      </div>
    );
  }

  const digits = phone.e164.replace('+', '');
  return (
    <div className={`crm-contact crm-contact-${size}`}>
      <a
        className="crm-contact-btn call"
        href={`tel:${phone.e164}`}
        data-tip={t('crm.contact.call')}
        aria-label={t('crm.contact.call')}
      >
        <Phone size={iconSize} />
      </a>
      <a
        className="crm-contact-btn whatsapp"
        // WhatsApp is the one that takes the text in the link, so it arrives typed.
        href={`https://wa.me/${digits}?text=${encodeURIComponent(greeting)}`}
        target="_blank"
        rel="noopener noreferrer"
        data-tip={t('crm.contact.writeOn', { app: 'WhatsApp' })}
        aria-label="WhatsApp"
        onClick={takeGreeting}
      >
        <WhatsAppIcon size={iconSize} />
      </a>
      <a
        className="crm-contact-btn viber"
        href={`viber://chat?number=${encodeURIComponent(phone.e164)}`}
        data-tip={t('crm.contact.writeOn', { app: 'Viber' })}
        aria-label="Viber"
        onClick={takeGreeting}
      >
        <ViberIcon size={iconSize} />
      </a>
      <a
        className="crm-contact-btn telegram"
        href={`https://t.me/${phone.e164}`}
        target="_blank"
        rel="noopener noreferrer"
        data-tip={t('crm.contact.writeOn', { app: 'Telegram' })}
        aria-label="Telegram"
        onClick={takeGreeting}
      >
        <TelegramIcon size={iconSize} />
      </a>
      {greetingButtons}
      <button
        type="button"
        className="crm-contact-btn copy"
        data-tip={copied === 'contact' ? t('crm.contact.copied') : t('crm.contact.copy')}
        aria-label={t('crm.contact.copy')}
        onClick={() => copy(phone.e164, 'contact')}
      >
        {copied === 'contact' ? <Check size={iconSize} /> : <Copy size={iconSize} />}
      </button>
      {status && <p className="crm-contact-status" role="status">{status}</p>}
    </div>
  );
}
